<template>
  <div class="uploaded-ads-page">
    <div class="page-toolbar">
      <h2 class="page-title">
        Uploaded Ads
      </h2>
      <div class="toolbar-actions">
        <b-form-input
          v-model="search"
          placeholder="Search"
          class="search-input"
        />
        <b-form-select
          v-model="statusFilter"
          :options="filterOptions"
          class="filter-select"
        />
      </div>
    </div>

    <div
      v-if="isLoading"
      class="state-panel"
    >
      <b-spinner label="Loading uploaded ads..." />
    </div>

    <div
      v-else-if="loadError"
      class="state-panel state-panel--error"
    >
      <p>{{ loadError }}</p>
      <b-button
        size="sm"
        variant="primary"
        @click="fetchUploadedAds"
      >
        Retry
      </b-button>
    </div>

    <div
      v-else-if="!filteredAds.length"
      class="state-panel state-panel--empty"
    >
      <feather-icon
        icon="ImageIcon"
        size="48"
        class="empty-icon"
      />
      <h4>No uploaded ads yet</h4>
      <p>Ads you publish will appear here.</p>
      <b-button
        variant="primary"
        :to="{ name: 'create-ad' }"
      >
        Create New Ad
      </b-button>
    </div>

    <div
      v-else
      class="uploaded-grid"
    >
      <div
        v-for="item in paginatedAds"
        :key="item.id"
        class="uploaded-card"
      >
        <img
          :src="item.image"
          alt="Ad preview"
          class="uploaded-image"
        >
        <span
          class="ad-state"
          :class="`status-${item.statusKey}`"
        >
          {{ item.status }}
        </span>
        <div class="uploaded-content">
          <div class="uploaded-head">
            <h5>{{ item.title }}</h5>
            <small>{{ item.createdAt }}</small>
          </div>
          <p class="uploaded-stats">
            {{ item.likes }} Likes · {{ item.dislikes }} Dislike · {{ item.reports }} Reports
          </p>
          <div class="uploaded-foot">
            <span>{{ item.views }} views · {{ item.shares }} Share · {{ item.saved }} Saved</span>
            <feather-icon
              v-if="!deletingIds.includes(item.id)"
              icon="Trash2Icon"
              size="14"
              class="cursor-pointer delete-icon"
              @click="deleteAd(item)"
            />
            <b-spinner
              v-else
              small
              label="Deleting"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="filteredAds.length && !isLoading"
      class="table-footer"
    >
      <small>Showing {{ rangeStart }} to {{ rangeEnd }} of {{ filteredAds.length }} entries</small>
      <b-pagination
        v-model="currentPage"
        :total-rows="filteredAds.length"
        :per-page="perPage"
        size="sm"
        first-number
        last-number
        class="mb-0"
      >
        <template #prev-text>
          Previous
        </template>
        <template #next-text>
          Next
        </template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BFormSelect,
  BButton,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import { confirmAction } from '@/utils/uiActions'
import { extractApiErrorMessage, normalizeAdStatusKey, formatCompactCount } from '@/utils/adCreate'
import { resolveMediaUrl } from '@/utils/mediaUrl'

const DEFAULT_AD_IMAGE = require('@/assets/images/slider/03.jpg')

export default {
  components: {
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BPagination,
  },
  data() {
    return {
      search: '',
      statusFilter: 'all',
      filterOptions: [
        { value: 'all', text: 'Select' },
        { value: 'active', text: 'Active' },
        { value: 'expired', text: 'Expired' },
        { value: 'pending', text: 'Pending' },
        { value: 'pending_approval', text: 'Pending approval' },
      ],
      uploadedAds: [],
      isLoading: false,
      loadError: '',
      deletingIds: [],
      currentPage: 1,
      perPage: 6,
    }
  },
  computed: {
    filteredAds() {
      const term = this.search.trim().toLowerCase()
      return this.uploadedAds.filter(item => {
        const matchesSearch = !term
          || `${item.title} ${item.status}`.toLowerCase().includes(term)
        const matchesFilter = this.statusFilter === 'all'
          || item.statusKey === this.statusFilter
        return matchesSearch && matchesFilter
      })
    },
    paginatedAds() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredAds.slice(start, start + this.perPage)
    },
    rangeStart() {
      if (!this.filteredAds.length) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    rangeEnd() {
      if (!this.filteredAds.length) return 0
      return Math.min(this.currentPage * this.perPage, this.filteredAds.length)
    },
  },
  watch: {
    search() {
      this.currentPage = 1
    },
    statusFilter() {
      this.currentPage = 1
    },
  },
  mounted() {
    this.fetchUploadedAds()
  },
  methods: {
    async fetchUploadedAds() {
      this.isLoading = true
      this.loadError = ''
      try {
        const response = await vendorPanelApi.uploadedAds({
          page: 1,
          per_page: 50,
          search: '',
          status: 'all',
        })
        const rows = response?.data?.data?.ads || []
        this.uploadedAds = rows.map(item => ({
          id: item.id,
          title: item.title || 'Ad Title',
          image: resolveMediaUrl(item.thumbnail || item.thambnail || item.image) || DEFAULT_AD_IMAGE,
          status: item.status || 'Active',
          statusKey: normalizeAdStatusKey(item.status || 'active'),
          createdAt: item.created_at || item.createdAt || '-',
          likes: formatCompactCount(item.likes ?? 0),
          dislikes: formatCompactCount(item.dislikes ?? 0),
          reports: formatCompactCount(item.reports ?? 0),
          views: formatCompactCount(item.views ?? 0),
          shares: formatCompactCount(item.shares ?? 0),
          saved: formatCompactCount(item.saved ?? 0),
        }))
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Unable to load uploaded ads.'
        this.uploadedAds = []
      } finally {
        this.isLoading = false
      }
    },
    deleteAd(item) {
      if (!item?.id || this.deletingIds.includes(item.id)) return
      confirmAction(
        this,
        {
          title: 'Delete ad?',
          text: 'This action cannot be undone.',
          confirmButtonText: 'Delete',
        },
        async () => {
          this.deletingIds.push(item.id)
          try {
            await vendorPanelApi.deleteAd({ ad_id: item.id })
            this.uploadedAds = this.uploadedAds.filter(row => row.id !== item.id)
            if (this.$toast) {
              this.$toast('Ad deleted')
            }
          } catch (error) {
            if (this.$swal) {
              this.$swal({
                title: 'Error',
                text: extractApiErrorMessage(error, 'Unable to delete ad'),
                icon: 'error',
                timer: 2200,
                showConfirmButton: false,
              })
            }
          } finally {
            this.deletingIds = this.deletingIds.filter(id => id !== item.id)
          }
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.uploaded-ads-page {
  .page-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  .page-title {
    margin: 0;
    color: #4b465c;
    font-size: 1.75rem;
    font-weight: 700;
  }

  .toolbar-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .search-input {
    min-width: 220px;
    max-width: 280px;
  }

  .filter-select {
    min-width: 140px;
    max-width: 180px;
  }

  .state-panel {
    min-height: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    color: #6e6b7b;

    &--error p {
      color: #ea5455;
      margin: 0;
    }

    &--empty .empty-icon {
      color: #b9b9c3;
      margin-bottom: 0.5rem;
    }
  }

  .uploaded-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }

  .uploaded-card {
    border: 1px solid #ececf3;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
    position: relative;
  }

  .uploaded-image {
    width: 100%;
    height: 160px;
    object-fit: cover;
  }

  .ad-state {
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.2rem 0.55rem;
    border-radius: 4px;
    text-transform: capitalize;

    &.status-active {
      background: rgba(40, 199, 111, 0.15);
      color: #28c76f;
    }

    &.status-pending,
    &.status-pending_approval {
      background: rgba(115, 103, 240, 0.16);
      color: #7367f0;
    }

    &.status-expired {
      background: rgba(255, 159, 67, 0.16);
      color: #ff9f43;
    }

    &.status-rejected {
      background: rgba(234, 84, 85, 0.12);
      color: #ea5455;
    }

    &.status-draft,
    &.status-inactive,
    &.status-stopped {
      background: rgba(130, 134, 139, 0.16);
      color: #82868b;
    }
  }

  .uploaded-content {
    padding: 0.85rem 1rem 1rem;
  }

  .uploaded-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.35rem;

    h5 {
      margin: 0;
      flex: 1;
      min-width: 0;
      font-size: 1rem;
      color: #4b465c;
    }

    small {
      flex-shrink: 0;
      white-space: nowrap;
      color: #a8aaae;
      font-size: 0.78rem;
    }
  }

  .uploaded-stats {
    margin: 0.5rem 0;
    font-size: 0.82rem;
    color: #6e6b7b;
  }

  .uploaded-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.78rem;
    color: #a8aaae;
  }

  .delete-icon {
    color: #ea5455;
  }

  .table-footer {
    margin-top: 1rem;
  }

  @media (max-width: 991px) {
    .uploaded-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 575px) {
    .uploaded-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
